// Basic save/load: one localStorage slot capturing everything that makes up "where
// you are" -- position, room mutations (melted ice, drained braziers), inventory,
// door locks, learned runes, and the notebook. Explicit only (Save/Load buttons in
// the Menu), no autosave -- "basic" was the ask.
import { NotebookState } from './notebook';
import { PlayerStatsSnapshot } from './playerStats';
import { RoomMutation } from '../../rune-lab/src/sim/types';

// RoomMutation moved into the sim module (2026-07-30) so that the pure
// serialize/apply pair that produces and consumes it can live there too, without
// the sim depending on `game/`. Re-exported here so existing importers of this
// module are unaffected.
export type { RoomMutation };

// The room/position slice only GameScene can produce or apply -- see
// registerSnapshotProvider/registerSnapshotApplier below.
export interface RoomSnapshot {
  roomId: string;
  tileX: number;
  tileY: number;
  facing: string;
  rooms: Record<string, RoomMutation>;
}

export interface SaveData extends RoomSnapshot {
  version: 1;
  savedAt: string;
  /** The runtime-object id counters (`WorldState.nextRockId`/`nextVineId`).
   *
   * Saved as of 2026-08-12, alongside `RoomMutation.changedObjects` making
   * runtime-created objects survivable at all. Without them a reload restarts
   * both counters at 1 while the restored room already contains a `rock-1`, so
   * the next split mints a duplicate id -- and an id is how every reference in
   * this world is held (the marked source, a sprite's `objectId`, a `bind`'s
   * `sourceId`), so a duplicate is two different objects the game cannot tell
   * apart. Optional because saves written before this lack it; `loadGame` then
   * derives a safe floor from the restored rooms rather than trusting 1. */
  nextRockId?: number;
  nextVineId?: number;
  inventory: string[];
  /** Item ids ever granted, kept even after `inventory` loses them (a one-use
   * key consumed unlocking its door) -- see inventory.ts's `collected()` doc
   * comment for the bug this exists to close. Optional only because saves
   * written before this existed lack it; ui.ts's loadGame() falls back to
   * `inventory` itself, same shape playerStats' own back-compat fallback
   * already has. */
  collectedItemIds?: string[];
  learnedRunes: string[];
  doorLocks: Record<string, boolean>;
  notebook: NotebookState;
  /** Optional only because saves written before Energy/Health existed lack it --
   * see ui.ts's loadGame(), which falls back to a fresh reset when absent. A
   * save from before the Mana/thermalState split (2026-08-05) still has this
   * field, just shaped like the old single pool (`{ energy, health, charge }`)
   * -- setPlayerStatsSnapshot maps that onto the new fields on load rather
   * than treating it as absent; see that function's own comment. */
  playerStats?: PlayerStatsSnapshot;
}

const STORAGE_KEY = 'rune-world-game-save';

export function hasSave(): boolean {
  return localStorage.getItem(STORAGE_KEY) !== null;
}

export function readSave(): SaveData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as SaveData;
  } catch {
    // Corrupt/unreadable storage -- treat as no save rather than throwing.
  }
  return null;
}

export function writeSave(data: SaveData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

// GameScene registers these two so ui.ts (which has no direct scene reference) can
// pull a room/position snapshot to save, and push a previously-loaded one back in to
// apply. Request/response shaped rather than fire-and-forget like onNewGame/
// onRoomJump, since a value actually has to come back out here, not just a signal in.
let snapshotProvider: (() => RoomSnapshot) | null = null;
let snapshotApplier: ((snapshot: RoomSnapshot) => void) | null = null;

export function registerSnapshotProvider(fn: () => RoomSnapshot): void {
  snapshotProvider = fn;
}

export function registerSnapshotApplier(fn: (snapshot: RoomSnapshot) => void): void {
  snapshotApplier = fn;
}

export function getRoomSnapshot(): RoomSnapshot | null {
  return snapshotProvider ? snapshotProvider() : null;
}

export function applyRoomSnapshot(snapshot: RoomSnapshot): void {
  snapshotApplier?.(snapshot);
}
